// DateRangeWithGenerate.jsx
import TimestampSummaryIndicator from '@/components/indicators/TimestampSummaryIndicator';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import { Box, Button, Popover, Tooltip } from '@mui/material';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { daDK } from '@mui/x-date-pickers/locales';
import { DateTime } from 'luxon';
import React from 'react';

/** Small date label helper (Danish) */
const formatDate = (date) => {
  if (!date) return '';
  const d = date.setLocale('da');
  return `${d.toFormat('d.')} ${d.toFormat('MMMM')} ${d.toFormat('yyyy')}`;
};

/** Build a few handy shortcuts */
function buildYearShortcuts(count = 4) {
  const now = DateTime.now();
  return Array.from({ length: count }, (_, i) => {
    const start = now.minus({ years: i }).startOf('year');
    const end = i === 0 ? now : start.endOf('year');
    return {
      label: i === 0 ? 'Dette år' : String(start.year),
      getValue: () => [start, end],
    };
  });
}
const defaultShortcuts = [
  ...buildYearShortcuts(2),
  {
    label: 'Sidste 7 dage',
    getValue: () => [
      DateTime.now().minus({ days: 7 }).startOf('day'),
      DateTime.now(),
    ],
  },
  {
    label: 'Sidste 4 uger',
    getValue: () => [
      DateTime.now().minus({ weeks: 4 }).startOf('day'),
      DateTime.now(),
    ],
  },
];

/**
 * DateRangeWithGenerate
 *
 * Props:
 * - startDt: Luxon DateTime
 * - endDt: Luxon DateTime
 * - onChangeRange: (startDt, endDt) => void  // called after user confirms
 * - onGenerate: (startMs, endMs) => void     // called after onChangeRange
 * - canGenerate: boolean                     // enable/disable “Generer graf”
 * - disabled?: boolean
 * - zone?: string (default 'Europe/Copenhagen')
 * - locale?: 'da' | 'en' | ...
 * - generateLabel?: string (default 'Generer graf')
 * - shortcuts?: Array<{label, getValue:()=>[DateTime,DateTime]}>  // optional
 */
export default function DateRangeWithGenerate({
  startDt,
  endDt,
  onChangeRange,
  onGenerate,
  canGenerate,
  disabled = false,
  zone = 'Europe/Copenhagen',
  locale = 'da',
  generateLabel = 'Generer graf',
  shortcuts = defaultShortcuts,
  timestampSummary,
}) {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [tempRange, setTempRange] = React.useState([startDt, endDt]);

  // Keep internal temp range in sync if parent changes
  React.useEffect(() => {
    setTempRange([startDt, endDt]);
  }, [endDt, startDt]); // toMillis guards identity changes

  const open = Boolean(anchorEl);
  const now = React.useMemo(() => DateTime.now().setZone(zone), [zone]);

  const handleOpen = (e) => {
    setTempRange([startDt, endDt]);
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  const handleConfirmAndGenerate = () => {
    const [s, e] = tempRange || [];
    if (!s || !e) return;

    // Round like your current logic
    const roundedStart = s.setZone(zone).startOf('day');
    let roundedEnd = e.setZone(zone).endOf('day');
    if (roundedEnd > now) roundedEnd = now;

    if (
      roundedStart <= now &&
      roundedEnd <= now &&
      roundedEnd >= roundedStart
    ) {
      onChangeRange?.(roundedStart, roundedEnd);
      onGenerate?.(roundedStart.toMillis(), roundedEnd.toMillis());
    }
    handleClose();
  };

  const triggerLabel =
    startDt && endDt
      ? `${formatDate(startDt.setZone(zone))} - ${formatDate(endDt.setZone(zone))}`
      : 'Vælg periode';

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      adapterLocale={locale}
      localeText={
        locale === 'da'
          ? daDK.components.MuiLocalizationProvider.defaultProps.localeText
          : undefined
      }
    >
      <Tooltip title="Vælg periode" enterDelay={500} placement="top">
        <span>
          <Button
            startIcon={<CalendarTodayRoundedIcon />}
            variant="contained"
            onClick={handleOpen}
            disabled={disabled}
            disableRipple
            // sx={{ ':hover': { background: 'none' } }}
          >
            {triggerLabel}
          </Button>
        </span>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ border: '1px solid', borderColor: 'divider' }}>
          <StaticDateRangePicker
            displayStaticWrapperAs="mobile"
            disableFuture
            calendars={2}
            value={tempRange}
            onChange={(range) => {
              if (!range || !range[0] || !range[1]) return;
              setTempRange(range);
            }}
            // Using day-level views for clarity; switch to hours if you need time-of-day
            views={['year', 'month', 'day']}
            dayOfWeekFormatter={(d) => d.toLocaleString({ weekday: 'short' })}
            slotProps={{
              shortcuts: { items: shortcuts, style: { borderRadius: 8 } },
              actionBar: { actions: [] },
            }}
          />

          <Box
            display="flex"
            justifyContent="flex-end"
            gap={1}
            sx={{ p: 2, backgroundColor: 'background.paper' }}
          >
            <Button onClick={handleClose}>Luk</Button>

            <Button
              variant="contained"
              onClick={handleConfirmAndGenerate}
              disabled={!tempRange?.[0] || !tempRange?.[1] || !canGenerate}
            >
              {generateLabel}
            </Button>
          </Box>
        </Box>
      </Popover>
    </LocalizationProvider>
  );
}
